import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import LoadingButton from '@mui/lab/LoadingButton'
import Paper from '@mui/material/Paper'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import GroupsIcon from '@mui/icons-material/Groups'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'

import PatientIcon from '@/components/ui/icons/Patient'
import { AuthUser } from '@/core/classes/Auth'
import { WaitingRoom, WaitingRoomActiveConsultation } from '@/core/models/waiting-room.model'
import { isStaff } from '@/utils/type-guards'

import { WaitedTime } from './WaitingConsultationsTable'

type InProgressConsultationsProps = {
  authUser?: AuthUser | null
  consultations: WaitingRoomActiveConsultation[]
  waitingRoom: WaitingRoom
  isJoining?: boolean
  joiningConsultationId?: string | null
  onJoin: (consultation: WaitingRoomActiveConsultation, assign: boolean) => void
  onRejoin: (consultation: WaitingRoomActiveConsultation) => void
  // SEHA (en-SA) read-only case preview; when omitted the list renders exactly as before
  onPreview?: (consultation: WaitingRoomActiveConsultation) => void
}

export function InProgressConsultations({
  authUser,
  consultations,
  waitingRoom,
  isJoining = false,
  joiningConsultationId,
  onJoin,
  onRejoin,
  onPreview,
}: InProgressConsultationsProps) {
  const { t } = useTranslation()
  const [selected, setSelected] = useState<WaitingRoomActiveConsultation | null>(null)
  const coPresenceEnabled = waitingRoom.coPresenceConfig?.enabled ?? false
  const allowAssign = waitingRoom.coPresenceConfig?.allowAssign ?? false
  const isDoctor = isStaff(authUser) && authUser.status === 'MEDECIN'

  if (!consultations.length) return null

  const closeDialog = () => setSelected(null)

  return (
    <Stack spacing={1.5} sx={{ p: '20px 16px', pb: 0 }} data-testid="waiting-room-in-progress">
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {t('page.waitingRoom.inProgress.title', { count: consultations.length })}
      </Typography>
      <TableContainer>
        <Table sx={{ borderCollapse: 'separate', borderSpacing: '0 10px', minWidth: 500 }}>
          <TableBody>
            {consultations.map((consultation) => {
              const isMine = consultation.medecinId === authUser?._id
              const canJoin = !isMine && isDoctor && coPresenceEnabled
              const isRowJoining = isJoining && joiningConsultationId === consultation._id

              return (
                <Paper key={consultation._id} component={TableRow}>
                  <TableCell sx={{ borderBottom: 'none', borderTopLeftRadius: '6px', borderBottomLeftRadius: '6px' }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {consultation.isGroup ? <GroupsIcon color="primary" /> : <PatientIcon />}
                      <span>{consultation.patient}</span>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ borderBottom: 'none' }}>
                    <Chip size="small" variant="outlined" color="primary" label={consultation.medecin || t('page.waitingRoom.inProgress.unassigned')} />
                  </TableCell>
                  <TableCell sx={{ borderBottom: 'none' }}>
                    <WaitedTime startDate={consultation.startedAt} />
                  </TableCell>
                  <TableCell align="right" sx={{ borderBottom: 'none', borderTopRightRadius: '6px', borderBottomRightRadius: '6px' }}>
                    {onPreview && (
                      <Tooltip title={t('page.waitingRoom.preview.button')}>
                        <IconButton
                          size="small"
                          onClick={() => onPreview(consultation)}
                          sx={{ mr: 1 }}
                          data-testid="waiting-room-preview-active-consultation"
                        >
                          <VisibilityOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {isMine ? (
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => onRejoin(consultation)}
                        data-testid="waiting-room-rejoin-consultation"
                      >
                        {t('page.waitingRoom.inProgress.rejoin')}
                      </Button>
                    ) : canJoin ? (
                      <LoadingButton
                        size="small"
                        variant="contained"
                        loading={isRowJoining}
                        disabled={isJoining && !isRowJoining}
                        onClick={() => setSelected(consultation)}
                        data-testid="waiting-room-join-consultation"
                      >
                        {t('page.waitingRoom.inProgress.join')}
                      </LoadingButton>
                    ) : null}
                  </TableCell>
                </Paper>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <JoinConsultationDialog
        allowAssign={allowAssign}
        consultation={selected}
        isJoining={isJoining && joiningConsultationId === selected?._id}
        onClose={closeDialog}
        onConfirm={(assign) => {
          if (!selected) return
          onJoin(selected, assign)
          closeDialog()
        }}
      />
    </Stack>
  )
}

function JoinConsultationDialog({
  allowAssign,
  consultation,
  isJoining,
  onClose,
  onConfirm,
}: {
  allowAssign: boolean
  consultation: WaitingRoomActiveConsultation | null
  isJoining: boolean
  onClose: () => void
  onConfirm: (assign: boolean) => void
}) {
  const { t } = useTranslation()
  const [mode, setMode] = useState<'join' | 'assign'>('join')

  useEffect(() => {
    if (consultation) setMode('join')
  }, [consultation])

  return (
    <Dialog open={!!consultation} onClose={onClose} data-testid="join-consultation-dialog">
      <DialogTitle>{t('page.waitingRoom.inProgress.dialog.title')}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: allowAssign ? 2 : 0 }}>
          {t('page.waitingRoom.inProgress.dialog.message', {
            patient: consultation?.patient ?? '',
            doctor: consultation?.medecin ?? '',
          })}
        </DialogContentText>
        {allowAssign && (
          <RadioGroup value={mode} onChange={(event) => setMode(event.target.value as 'join' | 'assign')}>
            <FormControlLabel
              value="join"
              control={<Radio data-testid="join-consultation-join-radio" />}
              label={t('page.waitingRoom.inProgress.dialog.mode.join')}
            />
            <FormControlLabel
              value="assign"
              control={<Radio data-testid="join-consultation-assign-radio" />}
              label={t('page.waitingRoom.inProgress.dialog.mode.assign')}
            />
          </RadioGroup>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common:button.cancel.label')}</Button>
        <LoadingButton loading={isJoining} variant="contained" onClick={() => onConfirm(mode === 'assign')} data-testid="join-consultation-confirm">
          {t('page.waitingRoom.inProgress.dialog.confirm')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
